import { useEffect, useRef, useState } from "react";
import { playClipSilently, speakInsteadOfClip } from "../lib/clipVoice";
import type { NewscastConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { tx } from "../lib/tx";

/** Build an SVG polyline path for a falling stock line across a 0..1 box. */
function chartPath(points: number[], w: number, h: number): string {
  if (points.length < 2) return "";
  const max = Math.max(...points);
  const min = Math.min(...points);
  const span = max - min || 1;
  return points
    .map((p, i) => {
      const x = (i / (points.length - 1)) * w;
      const y = h - ((p - min) / span) * h;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

/**
 * The office wall TV running a breaking-news bulletin: a DEEPFAKE anchor clip
 * (avatar provider) reports the ransomware attack while the bank's share price
 * collapses on an overlaid chart. If no clip is configured it falls back to the
 * anchor portrait + a TTS read of the same script, so the beat always plays.
 * Ends on "What happens next?" once the report finishes.
 */
export function Newscast({
  config,
  onContinue,
  onSkip,
}: {
  config: NewscastConfig;
  onContinue: () => void;
  onSkip?: () => void;
}) {
  const [done, setDone] = useState(false);
  const [playing, setPlaying] = useState(false);
  const mounted = useRef(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const stock = config.stock;
  const points = stock?.points ?? [100, 99, 97, 92, 84, 71, 63, 58, 54, 51];

  useEffect(() => {
    mounted.current = true;
    const finish = () => mounted.current && setDone(true);

    if (config.clipUrl && videoRef.current && !speakInsteadOfClip()) {
      const v = videoRef.current;
      v.src = config.clipUrl;
      v.muted = !audio.isEnabled();
      v.play()
        .then(() => mounted.current && setPlaying(true))
        .catch(() => {
          // autoplay blocked -> fall back to TTS read.
          void readScript();
        });
      v.addEventListener("ended", finish);
      v.addEventListener("error", () => void readScript());
      return () => {
        mounted.current = false;
        v.removeEventListener("ended", finish);
        try {
          v.pause();
        } catch {
          /* ignore */
        }
      };
    }

    if (config.clipUrl && videoRef.current) {
      videoRef.current.loop = true;
      playClipSilently(videoRef.current, config.clipUrl);
    }
    void readScript();
    return () => {
      mounted.current = false;
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function readScript() {
    if (!mounted.current) return;
    setPlaying(true);
    await audio.speak(config.script, {
      voice: config.voiceId || "coral",
      lang: config.language || "en-US",
      instructions:
        "Speak as a composed, professional television news anchor delivering a serious breaking-news report. Clear, measured, authoritative.",
    });
    if (!audio.isEnabled()) {
      await new Promise<void>((r) => window.setTimeout(r, Math.min(14000, 2000 + config.script.length * 36)));
    }
    if (mounted.current) setDone(true);
  }

  return (
    <div className="newscast">
      <div className="news-tv">
        <div className="news-bezel">
          <div className="news-screen">
            {/* live anchor / portrait */}
            <div className="news-feed">
              {config.clipUrl ? (
                <video ref={videoRef} className="news-video" playsInline preload="auto" poster={config.posterUrl} />
              ) : config.posterUrl ? (
                <img className="news-video" src={config.posterUrl} alt="" />
              ) : (
                <div className="news-noface">{"\u{1F4FA}"}</div>
              )}

              {/* falling stock chart inset */}
              {stock && (
                <div className="news-stock">
                  <div className="news-stock-h">
                    <strong>{stock.symbol}</strong>
                    <span className="news-stock-chg">{stock.change}</span>
                  </div>
                  <svg viewBox="0 0 120 48" preserveAspectRatio="none" className="news-stock-svg">
                    <path d={chartPath(points, 120, 48)} className="news-stock-line" />
                  </svg>
                </div>
              )}
            </div>

            {/* channel bug + LIVE */}
            <div className="news-bug">{config.channel ?? tx("NBC24")}</div>
            <div className="news-live">
              <span className="news-live-dot" /> {tx("LIVE")}
            </div>

            {/* lower third */}
            <div className="news-lower">
              <div className="news-breaking">{tx("BREAKING")}</div>
              <div className="news-headline">{config.lowerThird}</div>
              {config.anchorName && <div className="news-anchor">{config.anchorName}</div>}
            </div>

            {/* ticker */}
            {config.ticker && (
              <div className="news-ticker">
                <span>{config.ticker}</span>
                <span aria-hidden>{config.ticker}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {!done && <div className="news-status">{playing ? tx("On air…") : tx("Tuning in…")}</div>}

      {(done || (config.skipLabel && onSkip)) && (
        <div className="news-after">
          {done && (
            <button className="btn news-continue" onClick={onContinue}>
              {config.continueLabel ?? tx("What happens next?")}
            </button>
          )}
          {config.skipLabel && onSkip && (
            <button className="btn news-skip" onClick={onSkip}>
              {config.skipLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
